/**
 * Shared visual primitives for the channel-detail config tabs
 * (`WidgetConfigTab`, `WidgetAITab`, `WidgetEmbedTab`).
 *
 * These are local ports of the Storybook design parts
 * (`storybook/src/dashboard/experiment/channels/_parts.tsx`) — same look,
 * usable in the real dashboard. `WidgetStyleTab` keeps its own copies of
 * `Section`/`ColorField` etc. so it stays self-contained; the tabs added in
 * this milestone share the primitives below. Per ADR-0011 every visual
 * primitive comes from `@cometchat-ai/ui-dashboard`.
 */

import {
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Label,
  Switch,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@cometchat-ai/ui-dashboard";
import { ChevronDown, Copy, Info, type LucideIcon } from "../../components/icons";
import { useState } from "react";

/** Collapsible editor section card — mirrors the design's `Section`. */
export function Section({
  title,
  subtitle,
  icon: Icon,
  children,
  defaultOpen = false,
  testId,
  flush = false,
}: {
  title: string;
  subtitle?: string;
  icon?: LucideIcon;
  children: React.ReactNode;
  defaultOpen?: boolean;
  /** Optional test id, placed on the disclosure trigger so tests can expand
   *  the section (replaces the old `<details>/<summary>` testId hook). */
  testId?: string;
  /** Borderless variant: no card border, header/content sit flush to the
   *  container edges. Used inside a single card where sibling `<Section>`s are
   *  separated by dashed dividers rather than each carrying its own box. */
  flush?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  // `subtitle` is accepted for call-site parity but, per the Final
  // `dashboard/final` channel-detail Section, no longer rendered.
  void subtitle;

  // Flush variant is a flat, always-expanded section (no accordion) for the
  // single-card create layout.
  if (flush) {
    return (
      <div>
        <div className="flex items-center gap-3 py-3">
          {Icon ? (
            <Icon className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          ) : null}
          <span className="min-w-0 flex-1 text-[13px] font-semibold text-foreground">{title}</span>
        </div>
        <div className="px-0 pb-4 pt-2">{children}</div>
      </div>
    );
  }

  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      className="overflow-hidden rounded-xl border border-border"
    >
      <CollapsibleTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          {...(testId ? { "data-testid": testId } : {})}
          aria-label={open ? `Collapse ${title}` : `Expand ${title}`}
          className="flex h-auto w-full items-center justify-start gap-3 whitespace-normal rounded-none px-4 py-3 text-left font-normal hover:bg-transparent"
        >
          {Icon ? (
            <Icon className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          ) : null}
          <span className="min-w-0 flex-1 text-[13px] font-semibold text-foreground">{title}</span>
          <ChevronDown
            className="h-4 w-4 shrink-0 text-[color:var(--color-fg-subtle)] transition-transform"
            style={{ transform: open ? "rotate(0deg)" : "rotate(-90deg)" }}
            aria-hidden="true"
          />
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="border-t border-border px-4 py-4">{children}</div>
      </CollapsibleContent>
    </Collapsible>
  );
}

/** Inline info tooltip for a label or toggle row. */
export function InfoHint({ hint }: { hint: string }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="text-muted-foreground">
          <Info className="h-3.5 w-3.5" aria-hidden="true" />
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">{hint}</TooltipContent>
    </Tooltip>
  );
}

/** Field label with an optional info tooltip. */
export function WidgetLabel({
  htmlFor,
  label,
  hint,
}: {
  htmlFor?: string;
  label: string;
  hint?: string;
}) {
  return (
    <div className="mb-1.5 flex items-center gap-1.5">
      <Label htmlFor={htmlFor} className="text-[13px] font-medium text-foreground">
        {label}
      </Label>
      {hint ? <InfoHint hint={hint} /> : null}
    </div>
  );
}

/** Label-left, switch-right row. Controlled. */
export function ToggleRow({
  label,
  hint,
  checked,
  onCheckedChange,
  testId,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  onCheckedChange: (value: boolean) => void;
  testId?: string;
}) {
  return (
    <div className="flex items-center justify-between gap-4">
      <div className="flex min-w-0 items-center gap-1.5">
        <p className="truncate text-[13px] font-medium text-foreground">{label}</p>
        {hint ? <InfoHint hint={hint} /> : null}
      </div>
      <Switch checked={checked} onCheckedChange={onCheckedChange} data-testid={testId} />
    </div>
  );
}

/** Muted helper bar with an info icon — sits under a field. */
export function WidgetHint({ children }: { children: React.ReactNode }) {
  return (
    <div className="mt-2 flex items-start gap-2 rounded-lg bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
      <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      <span>{children}</span>
    </div>
  );
}

/** Read-only, line-numbered code block with copy. */
export function CodeBlock({ code, copyLabel }: { code: string; copyLabel: string }) {
  const lines = code.split("\n");
  function onCopy() {
    void navigator.clipboard?.writeText(code);
  }
  return (
    <div className="overflow-hidden rounded-lg border border-border bg-[color:color-mix(in_oklch,var(--color-fg)_3%,transparent)]">
      <div className="overflow-x-auto px-3 py-3 font-mono text-xs leading-relaxed [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {lines.map((line, i) => (
          <div key={i} className="flex gap-4 whitespace-pre">
            <span className="w-5 shrink-0 select-none text-right text-muted-foreground">
              {i + 1}
            </span>
            <span className="text-foreground">{line || " "}</span>
          </div>
        ))}
      </div>
      <div className="flex justify-end border-t border-border px-3 py-2">
        <Button
          type="button"
          variant="secondary"
          onClick={onCopy}
          className="h-7 gap-1.5 rounded-md text-xs hover:bg-secondary-hover"
        >
          <Copy className="h-3.5 w-3.5" />
          {copyLabel}
        </Button>
      </div>
    </div>
  );
}
